import {
  DeviceCapability,
  DeviceKind,
  DeviceLiveness,
} from '@hapana/shared/schemas/checkins/device-kind-schemas'
import {
  FORM_HANDOFF_CHANNEL,
  FORM_HANDOFF_RESULT_CODE,
  FORM_HANDOFF_STATUS,
  isFormHandoffTerminal,
  type FormHandoffChannel,
  type FormHandoffStatus,
} from '@hapana/shared/schemas/site-forms/form-handoff-schemas'
import { FORM_HANDOFF_IDENTITY_MAX_ATTEMPTS } from '@hapana/shared/schemas/site-forms/form-handoff-identity'

import type { FormHandoffView } from '_services/form-handoffs/form-handoffs.service'
import type { SiteDevice } from '_services/devices/kiosk-devices.service'

export const TRACKER_STAGE = {
  SENT: 'SENT',
  ON_SCREEN: 'ON_SCREEN',
  OPENED: 'OPENED',
  IN_PROGRESS: 'IN_PROGRESS',
  COMPLETED: 'COMPLETED',
  DECLINED: 'DECLINED',
  EXPIRED: 'EXPIRED',
  CANCELLED: 'CANCELLED',
  FAILED: 'FAILED',
} as const

export type TrackerStage = (typeof TRACKER_STAGE)[keyof typeof TRACKER_STAGE]

const STAGE_BY_STATUS: Record<FormHandoffStatus, TrackerStage> = {
  SENT: TRACKER_STAGE.SENT,
  DELIVERED: TRACKER_STAGE.SENT,
  SHOWN: TRACKER_STAGE.ON_SCREEN,
  OPENED: TRACKER_STAGE.OPENED,
  IN_PROGRESS: TRACKER_STAGE.IN_PROGRESS,
  COMPLETED: TRACKER_STAGE.COMPLETED,
  DECLINED: TRACKER_STAGE.DECLINED,
  EXPIRED: TRACKER_STAGE.EXPIRED,
  CANCELLED: TRACKER_STAGE.CANCELLED,
  FAILED: TRACKER_STAGE.FAILED,
}

export const resolveTrackerStage = (handoff: Pick<FormHandoffView, 'status'>): TrackerStage =>
  STAGE_BY_STATUS[handoff.status]

const KIOSK_TRACKER_STEPS: readonly TrackerStage[] = [
  TRACKER_STAGE.SENT,
  TRACKER_STAGE.ON_SCREEN,
  TRACKER_STAGE.IN_PROGRESS,
  TRACKER_STAGE.COMPLETED,
]

const REMOTE_TRACKER_STEPS: readonly TrackerStage[] = [
  TRACKER_STAGE.SENT,
  TRACKER_STAGE.OPENED,
  TRACKER_STAGE.IN_PROGRESS,
  TRACKER_STAGE.COMPLETED,
]

export const listTrackerSteps = (channel: FormHandoffChannel): readonly TrackerStage[] =>
  channel === FORM_HANDOFF_CHANNEL.KIOSK ? KIOSK_TRACKER_STEPS : REMOTE_TRACKER_STEPS

export const resolveReachedStepCount = (
  handoff: Pick<FormHandoffView, 'channel' | 'status'>
): number => {
  const steps = listTrackerSteps(handoff.channel)
  const index = steps.indexOf(resolveTrackerStage(handoff))
  return index + 1
}

export const TRACKER_ACTION = {
  SEND_AGAIN: 'SEND_AGAIN',
  SEND_TO_APP: 'SEND_TO_APP',
  CANCEL: 'CANCEL',
} as const

export type TrackerAction = (typeof TRACKER_ACTION)[keyof typeof TRACKER_ACTION]

export const listTrackerActions = (input: {
  channel: FormHandoffChannel
  status: FormHandoffStatus
  canSendToApp: boolean
}): TrackerAction[] => {
  if (input.status === FORM_HANDOFF_STATUS.COMPLETED) {
    return []
  }
  const offersApp = input.canSendToApp && input.channel === FORM_HANDOFF_CHANNEL.KIOSK
  if (isFormHandoffTerminal(input.status)) {
    return offersApp
      ? [TRACKER_ACTION.SEND_AGAIN, TRACKER_ACTION.SEND_TO_APP]
      : [TRACKER_ACTION.SEND_AGAIN]
  }
  return offersApp ? [TRACKER_ACTION.SEND_TO_APP, TRACKER_ACTION.CANCEL] : [TRACKER_ACTION.CANCEL]
}

const PROGRESS_HIDDEN_STAGES: ReadonlySet<TrackerStage> = new Set([TRACKER_STAGE.SENT])

export const resolveTrackerProgress = (
  handoff: Pick<FormHandoffView, 'status' | 'answeredCount' | 'questionCount'>
): { answered: number; total: number } | null =>
  !isFormHandoffTerminal(handoff.status) &&
  !PROGRESS_HIDDEN_STAGES.has(resolveTrackerStage(handoff)) &&
  handoff.questionCount &&
  handoff.questionCount > 0
    ? {
        answered: Math.min(handoff.answeredCount ?? 0, handoff.questionCount),
        total: handoff.questionCount,
      }
    : null

export const HANDOFF_POLL_BASE_MS = 5_000
export const HANDOFF_POLL_JITTER_MS = 1_000

export const computeHandoffPollDelayMs = (random: number): number =>
  HANDOFF_POLL_BASE_MS + Math.round(Math.min(Math.max(random, 0), 1) * HANDOFF_POLL_JITTER_MS)

export const KIOSK_AVAILABILITY = {
  AVAILABLE: 'AVAILABLE',
  BUSY: 'BUSY',
  NOT_RESPONDING: 'NOT_RESPONDING',
  NEVER_CONNECTED: 'NEVER_CONNECTED',
} as const

export type KioskAvailability = (typeof KIOSK_AVAILABILITY)[keyof typeof KIOSK_AVAILABILITY]

export const resolveKioskAvailability = (
  device: Pick<SiteDevice, 'liveness' | 'busy'>
): KioskAvailability => {
  if (device.liveness === DeviceLiveness.OFFLINE_NEVER_SEEN) {
    return KIOSK_AVAILABILITY.NEVER_CONNECTED
  }
  if (device.liveness === DeviceLiveness.STALE) {
    return KIOSK_AVAILABILITY.NOT_RESPONDING
  }
  return device.busy ? KIOSK_AVAILABILITY.BUSY : KIOSK_AVAILABILITY.AVAILABLE
}

export const resolveTrackerTitleKey = (
  handoff: Pick<FormHandoffView, 'channel' | 'status'>
): string =>
  isFormHandoffTerminal(handoff.status)
    ? `titleByStage.${resolveTrackerStage(handoff)}`
    : `title.${handoff.channel}`

export const listFormKiosks = (devices: readonly SiteDevice[]): SiteDevice[] =>
  devices.filter(
    (device) =>
      device.deviceKind === DeviceKind.KIOSK && device.capabilities.includes(DeviceCapability.FORMS)
  )

const KIOSK_DEFAULT_STORAGE_PREFIX = 'hapana:kiosk-default:'

export const buildKioskDefaultStorageKey = (siteId: string): string =>
  `${KIOSK_DEFAULT_STORAGE_PREFIX}${siteId}`

export const readDefaultKioskId = (storage: Storage | undefined, siteId: string): string | null => {
  try {
    return storage?.getItem(buildKioskDefaultStorageKey(siteId)) ?? null
  } catch {
    return null
  }
}

export const writeDefaultKioskId = (
  storage: Storage | undefined,
  siteId: string,
  deviceTokenId: string | null
): void => {
  try {
    if (deviceTokenId) {
      storage?.setItem(buildKioskDefaultStorageKey(siteId), deviceTokenId)
    } else {
      storage?.removeItem(buildKioskDefaultStorageKey(siteId))
    }
  } catch {
    return
  }
}

export const readBrowserStorage = (): Storage | undefined => {
  try {
    return window.localStorage
  } catch {
    return undefined
  }
}

const isKioskAvailable = (kiosk: SiteDevice): boolean =>
  resolveKioskAvailability(kiosk) === KIOSK_AVAILABILITY.AVAILABLE

export const resolvePreselectedKioskId = (
  kiosks: readonly SiteDevice[],
  storedId: string | null
): string => {
  const available = kiosks.filter(isKioskAvailable)
  if (storedId && available.some((kiosk) => kiosk.deviceTokenId === storedId)) {
    return storedId
  }
  return available.length === 1 ? (available[0]?.deviceTokenId ?? '') : ''
}

export const resolveRefreshedKioskId = (
  kiosks: readonly SiteDevice[],
  currentId: string | null | undefined,
  storedId: string | null
): string | null => {
  if (isKioskSelectable(kiosks, currentId)) {
    return null
  }
  return resolvePreselectedKioskId(kiosks, storedId) || null
}

export const NO_KIOSK_REASON = {
  LOADING: 'LOADING',
  LOAD_FAILED: 'LOAD_FAILED',
  NONE_PAIRED: 'NONE_PAIRED',
  DEFAULT_BUSY: 'DEFAULT_BUSY',
  DEFAULT_NOT_RESPONDING: 'DEFAULT_NOT_RESPONDING',
  CHOOSE_DEFAULT: 'CHOOSE_DEFAULT',
  ALL_BUSY: 'ALL_BUSY',
  NOT_RESPONDING: 'NOT_RESPONDING',
} as const

export type NoKioskReason = (typeof NO_KIOSK_REASON)[keyof typeof NO_KIOSK_REASON]

export const resolveNoKioskReason = (input: {
  kiosks: readonly SiteDevice[]
  storedId: string | null
  isLoading: boolean
  isError?: boolean
}): NoKioskReason | null => {
  if (input.isLoading) {
    return NO_KIOSK_REASON.LOADING
  }
  if (input.isError) {
    return NO_KIOSK_REASON.LOAD_FAILED
  }
  if (resolvePreselectedKioskId(input.kiosks, input.storedId)) {
    return null
  }
  if (input.kiosks.length === 0) {
    return NO_KIOSK_REASON.NONE_PAIRED
  }
  if (input.kiosks.filter(isKioskAvailable).length > 1) {
    const storedKiosk = input.kiosks.find((kiosk) => kiosk.deviceTokenId === input.storedId)
    if (!storedKiosk) {
      return NO_KIOSK_REASON.CHOOSE_DEFAULT
    }
    return resolveKioskAvailability(storedKiosk) === KIOSK_AVAILABILITY.BUSY
      ? NO_KIOSK_REASON.DEFAULT_BUSY
      : NO_KIOSK_REASON.DEFAULT_NOT_RESPONDING
  }
  return input.kiosks.some((kiosk) => resolveKioskAvailability(kiosk) === KIOSK_AVAILABILITY.BUSY)
    ? NO_KIOSK_REASON.ALL_BUSY
    : NO_KIOSK_REASON.NOT_RESPONDING
}

export const hasAvailableKiosk = (kiosks: readonly SiteDevice[]): boolean =>
  kiosks.some(isKioskAvailable)

export const isKioskSelectable = (
  kiosks: readonly SiteDevice[],
  deviceTokenId: string | null | undefined
): boolean =>
  Boolean(deviceTokenId) &&
  kiosks.some((kiosk) => kiosk.deviceTokenId === deviceTokenId && isKioskAvailable(kiosk))

export const buildKioskLabelMap = (devices: readonly SiteDevice[]): Record<string, string> =>
  Object.fromEntries(
    devices.flatMap((device) => {
      const label = device.label?.trim()
      return label ? [[device.deviceTokenId, label]] : []
    })
  )

export const findKioskLabel = (
  kiosks: readonly SiteDevice[],
  deviceTokenId: string | null | undefined
): string | null =>
  kiosks.find((kiosk) => kiosk.deviceTokenId === deviceTokenId)?.label?.trim() || null

const PILL_BY_STAGE: Record<TrackerStage, string> = {
  SENT: 'scheduled',
  ON_SCREEN: 'scheduled',
  OPENED: 'scheduled',
  IN_PROGRESS: 'in_use',
  COMPLETED: 'paid',
  DECLINED: 'failed',
  EXPIRED: 'failed_canceled',
  CANCELLED: 'canceled',
  FAILED: 'failed',
}

export const resolveTrackerPill = (stage: TrackerStage): string => PILL_BY_STAGE[stage]

export const resolveTrackerDetailKey = (
  handoff: Pick<FormHandoffView, 'status' | 'resultCode' | 'channel'>
): string | null => {
  if (
    !isFormHandoffTerminal(handoff.status) ||
    handoff.status === FORM_HANDOFF_STATUS.COMPLETED ||
    !handoff.resultCode
  ) {
    return null
  }
  const isLinkDeliveryFailure =
    handoff.resultCode === FORM_HANDOFF_RESULT_CODE.PROVIDER_FAILED &&
    handoff.channel === FORM_HANDOFF_CHANNEL.PUBLIC_LINK
  return isLinkDeliveryFailure
    ? 'resultCodes.PROVIDER_FAILED_LINK'
    : `resultCodes.${handoff.resultCode}`
}

type IdentityAwareHandoff = Pick<FormHandoffView, 'channel' | 'status' | 'identityConfirmedAt'>

export const isAwaitingKioskIdentity = (
  handoff: IdentityAwareHandoff | undefined,
  isIdentityFlagOn: boolean
): boolean =>
  isIdentityFlagOn &&
  handoff?.channel === FORM_HANDOFF_CHANNEL.KIOSK &&
  handoff.status === FORM_HANDOFF_STATUS.SHOWN &&
  !handoff.identityConfirmedAt

export type TrackerIdentityHint = { attemptsUsed: number | null; maxAttempts: number }

export const resolveTrackerIdentityHint = (
  handoff: IdentityAwareHandoff & Pick<FormHandoffView, 'identityFailedAttempts'>,
  isIdentityFlagOn: boolean
): TrackerIdentityHint | null => {
  if (!isAwaitingKioskIdentity(handoff, isIdentityFlagOn)) {
    return null
  }
  const attemptsUsed = Math.min(
    Math.max(handoff.identityFailedAttempts ?? 0, 0),
    FORM_HANDOFF_IDENTITY_MAX_ATTEMPTS
  )
  return {
    attemptsUsed: attemptsUsed > 0 ? attemptsUsed : null,
    maxAttempts: FORM_HANDOFF_IDENTITY_MAX_ATTEMPTS,
  }
}

const HANDOFF_ERROR_KEYS: Partial<Record<string, string>> = {
  DEVICE_BUSY: 'deviceBusy',
  DEVICE_OFFLINE: 'deviceOffline',
  DEVICE_REVOKED: 'deviceRevoked',
  DEVICE_CAPABILITY_REQUIRED: 'deviceCapabilityRequired',
  HANDOFF_NOT_CURRENT: 'notCurrent',
  HANDOFF_EXPIRED: 'notCurrent',
  FORM_ALREADY_COMPLETED: 'alreadyCompleted',
  FORM_ASSIGNMENT_CANCELLED: 'withdrawn',
  REQUIREMENT_CHANGE_REQUIRED: 'requirementChange',
  FEATURE_UNAVAILABLE: 'featureUnavailable',
  FEATURE_NOT_ENTITLED: 'featureUnavailable',
  IDEMPOTENCY_CONFLICT: 'idempotencyConflict',
}

export const resolveHandoffErrorKey = (code: string | null): string =>
  `errors.${(code && HANDOFF_ERROR_KEYS[code]) || 'generic'}`
